import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { authApi, isChallenge } from '@/api/authApi'
import i18n from '@/i18n/i18n'
import {
  clearAuth,
  getSession,
  isAuthenticated as hasToken,
  setAuth,
  setSession,
  type AuthSession,
} from '@/lib/authToken'

interface AuthContextValue {
  isAuthenticated: boolean
  /** The resolved principal, or null until `/auth/me` answers. */
  user: AuthSession | null
  /** Email-only dev login (VITE_AUTH_DEV_LOGIN). */
  devLogin: (email: string) => Promise<void>
  /** Cognito email/password sign-in. */
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  /** Re-fetch `/auth/me`. */
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [authed, setAuthed] = useState<boolean>(hasToken())
  const [user, setUser] = useState<AuthSession | null>(getSession())

  const loadMe = useCallback(async () => {
    const me = await authApi.me()
    setSession(me)
    setUser(me)
  }, [])

  // With a live token on mount, refresh the principal so permissions are current.
  // A 401 here is handled by request() (sign-out + redirect).
  useEffect(() => {
    if (!authed) return
    loadMe().catch(() => undefined)
  }, [authed, loadMe])

  const startSession = useCallback(
    async (tokens: { accessToken: string; refreshToken?: string }) => {
      setAuth(tokens)
      try {
        await loadMe()
      } catch (err) {
        clearAuth()
        throw err
      }
      setAuthed(true)
    },
    [loadMe],
  )

  const devLogin = useCallback(
    async (email: string) => startSession(await authApi.devLogin(email)),
    [startSession],
  )

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await authApi.signin(email, password)
      // The set-new-password flow is not part of the desk UI yet.
      if (isChallenge(res)) throw new Error(i18n.t('login.newPasswordRequired'))
      await startSession(res)
    },
    [startSession],
  )

  const logout = useCallback(() => {
    clearAuth()
    setUser(null)
    setAuthed(false)
    queryClient.clear()
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({ isAuthenticated: authed, user, devLogin, login, logout, refresh: loadMe }),
    [authed, user, devLogin, login, logout, loadMe],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
